import { useQuery } from '@tanstack/react-query';
import { Check, Copy, Eye, EyeOff, Loader2 } from 'lucide-react';
import React, { useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/useToast';
import { partnerApi, type Partner } from '../../lib/api';
import { SETTINGS_STRINGS } from '../../lib/constants';

// Utility function to base64url encode a string
const base64UrlEncode = (str: string): string => {
  const base64 = btoa(str);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

// Utility function to convert a string to Uint8Array
const stringToUint8Array = (str: string) => {
  const encoder = new TextEncoder();
  return encoder.encode(str);
};

// Async function to generate a JWT token using Web Crypto API
const generateJWT = async (
  payload: object,
  secret: string,
): Promise<string> => {
  try {
    const header = {
      alg: 'HS256',
      typ: 'JWT',
    };

    const encodedHeader = base64UrlEncode(JSON.stringify(header));
    const encodedPayload = base64UrlEncode(JSON.stringify(payload));
    const dataToSign = `${encodedHeader}.${encodedPayload}`;

    const encoder = new TextEncoder();
    const keyData = encoder.encode(secret);
    const cryptoKey = await window.crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign'],
    );

    const dataBuffer = stringToUint8Array(dataToSign);
    const signature = await window.crypto.subtle.sign(
      'HMAC',
      cryptoKey,
      dataBuffer,
    );

    const signatureArray = Array.from(new Uint8Array(signature));
    const signatureString = String.fromCharCode(...signatureArray);
    const encodedSignature = base64UrlEncode(signatureString);

    return `${encodedHeader}.${encodedPayload}.${encodedSignature}`;
  } catch (error) {
    console.error('Error generating JWT token:', error);
    throw error;
  }
};

/**
 * API Configuration Tab Component
 * Allows partners to view JWT secret and generate API tokens
 */
export const ApiTab: React.FC = () => {
  const { addToast } = useToast();
  const [showJwtSecret, setShowJwtSecret] = useState(false);
  const [showApiToken, setShowApiToken] = useState(false);
  const [apiToken, setApiToken] = useState<string>('');
  const [isGeneratingToken, setIsGeneratingToken] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const { data: partner, isLoading } = useQuery<Partner>({
    queryKey: ['partner'],
    queryFn: () => partnerApi.getPartner(),
  });

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
    addToast({
      variant: 'success',
      title: SETTINGS_STRINGS.API_CONFIGURATION.COPY_SUCCESS,
    });
  };

  const generateApiToken = async () => {
    if (!partner?.jwtSecret) {
      setTokenError('Partner does not have a JWT secret configured');
      return;
    }

    setIsGeneratingToken(true);
    setTokenError(null);

    try {
      const payload = {
        platformId: partner.id,
        type: 'platform',
      };

      const token = await generateJWT(payload, partner.jwtSecret);
      setApiToken(token);
    } catch (error) {
      console.error('Error generating API token:', error);
      setTokenError(
        'Failed to generate API token: ' +
          (error instanceof Error ? error.message : String(error)),
      );
    } finally {
      setIsGeneratingToken(false);
    }
  };

  const obfuscateText = (text: string, showFull: boolean): string => {
    if (!text) return 'Not available';
    if (showFull) return text;
    return '••••••••••••••••';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* API Credentials */}
      <Card>
        <CardContent className="pt-6 space-y-6">
          {/* Partner ID */}
          <div className="space-y-2">
            <Label htmlFor="partnerId">Partner ID</Label>
            <div className="flex gap-2">
              <Input
                id="partnerId"
                type="text"
                value={partner?.id || ''}
                readOnly
                className="font-mono flex-1"
              />
              <button
                type="button"
                onClick={() => partner?.id && handleCopy(partner.id, 'id')}
                className="inline-flex items-center justify-center h-10 w-10 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground">
                {copied === 'id' ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          {/* Partner Hash */}
          <div className="space-y-2">
            <Label htmlFor="partnerHash">Partner Hash</Label>
            <div className="flex gap-2">
              <Input
                id="partnerHash"
                type="text"
                value={partner?.hash || ''}
                readOnly
                className="font-mono flex-1"
              />
              <button
                type="button"
                onClick={() =>
                  partner?.hash && handleCopy(partner.hash, 'hash')
                }
                className="inline-flex items-center justify-center h-10 w-10 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground">
                {copied === 'hash' ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          {/* JWT Secret */}
          <div className="space-y-2">
            <Label htmlFor="jwtSecret">JWT Secret</Label>
            <div className="flex gap-2">
              <Input
                id="jwtSecret"
                type={showJwtSecret ? 'text' : 'password'}
                value={
                  partner?.jwtSecret
                    ? showJwtSecret
                      ? partner.jwtSecret
                      : obfuscateText(partner.jwtSecret, false)
                    : 'Not available'
                }
                readOnly
                className="font-mono flex-1"
              />
              <button
                type="button"
                onClick={() => setShowJwtSecret(!showJwtSecret)}
                className="inline-flex items-center justify-center h-10 w-10 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground">
                {showJwtSecret ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
              <button
                type="button"
                onClick={() =>
                  partner?.jwtSecret && handleCopy(partner.jwtSecret, 'jwt')
                }
                className="inline-flex items-center justify-center h-10 w-10 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground">
                {copied === 'jwt' ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </button>
            </div>
            {!partner?.jwtSecret && (
              <p className="text-xs text-muted-foreground">
                No JWT secret available. Contact support to configure
                authentication.
              </p>
            )}
          </div>

          {/* API Token */}
          <div className="space-y-2">
            <Label htmlFor="apiToken">API Token</Label>
            {apiToken ? (
              <div className="flex gap-2">
                <Input
                  id="apiToken"
                  type={showApiToken ? 'text' : 'password'}
                  value={
                    showApiToken ? apiToken : obfuscateText(apiToken, false)
                  }
                  readOnly
                  className="font-mono flex-1"
                />
                <button
                  type="button"
                  onClick={() => setShowApiToken(!showApiToken)}
                  className="inline-flex items-center justify-center h-10 w-10 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground">
                  {showApiToken ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => handleCopy(apiToken, 'token')}
                  className="inline-flex items-center justify-center h-10 w-10 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground">
                  {copied === 'token' ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </button>
              </div>
            ) : (
              <div className="border rounded-md p-4 bg-muted/50">
                <p className="text-sm text-muted-foreground mb-2">
                  Generate a JWT token with these claims:
                </p>
                <pre className="bg-background p-3 rounded text-xs mb-3 overflow-x-auto">
                  {`{
  "platformId": "${partner?.id}",
  "type": "platform"
}`}
                </pre>
                <button
                  onClick={generateApiToken}
                  disabled={isGeneratingToken || !partner?.jwtSecret}
                  className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2">
                  {isGeneratingToken ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    'Generate API Token'
                  )}
                </button>
                {tokenError && (
                  <p className="mt-2 text-xs text-destructive">{tokenError}</p>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Information Card */}
      <Card>
        <CardContent className="pt-6">
          <h3 className="text-base font-semibold text-foreground mb-4">
            About API Authentication
          </h3>
          <div className="text-sm text-muted-foreground space-y-3">
            <div>
              <p className="font-medium mb-2">How it works:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  API tokens are generated using JWT (JSON Web Token) standard
                </li>
                <li>
                  The token contains your partner ID and is signed with your JWT
                  secret
                </li>
                <li>
                  Tokens can be used to authenticate API requests to Reach
                </li>
                <li>Keep your JWT secret secure and never share it publicly</li>
                <li>
                  You can generate new tokens as needed without affecting
                  existing ones
                </li>
              </ul>
            </div>
            <div className="pt-2 border-t">
              <p className="font-medium mb-1">Learn more:</p>
              <a
                href="https://docs.embedreach.com/samples/jwt-tokens"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline inline-flex items-center gap-1">
                View JWT implementation guide and examples
                <svg
                  className="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </a>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
